import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

// Keep status-based code in comments for future use
/*
const colors = ['#284F43', '#4A7C59', '#6BA772', '#8CD18D', '#AEEBA7']
const statuses = ['Unassigned', 'Investigating', 'Adjudicated', 'Resolved', 'Default']

interface ChartData {
  Unassigned: number
  Investigating: number
  Adjudicated: number
  Resolved: number
  Default: number
}
*/

interface ChartData {
  date: string
  value: number
}

interface Props {
  data: Record<string, number>
  title: string
}

export const StackedBarChart = ({ data, title }: Props) => {
  const [chartData, setChartData] = useState<ChartData[]>([])
  const [maxValue, setMaxValue] = useState(0)
  const [yTicks, setYTicks] = useState<number[]>([])

  const getNiceStep = (targetStep: number): number => {
    if (!isFinite(targetStep) || targetStep <= 0) return 1
    const magnitude = Math.pow(10, Math.floor(Math.log10(targetStep)))
    const normalized = targetStep / magnitude
    const multiples = [1, 2, 2.5, 5, 10]
    const chosen = multiples.find(m => normalized <= m) ?? 10
    return chosen * magnitude
  }

  useEffect(() => {
    // Keep status-based code in comments for future use
    /*
    const dict: Record<string, ChartData> = {}
    statuses.forEach(status => {
      Object.keys(data).forEach(key => {
        if (!dict[key]) {
          dict[key] = {
            Unassigned: 0,
            Investigating: 0,
            Adjudicated: 0,
            Resolved: 0,
            Default: 0,
          }
        }
        const statusData = data[key].find((item: any) => item.status === status)
        if (statusData) {
          dict[key][status] = statusData.value
        }
      })
    })
    */

    // Simple date-to-count mapping
    const dict: Record<string, ChartData> = {}

    Object.entries(data).forEach(([key, value]) => {
      dict[key] = {
        date: dayjs(key).format('MMM YYYY'),
        value,
      }
    })

    const chartDataArray = Object.values(dict)
    setChartData(chartDataArray)

    // Calculate max value and add 20% padding to prevent cutoff
    const max = Math.max(...chartDataArray.map(item => item.value), 0)
    const paddedMax = Math.ceil(max * 1.2)
    setMaxValue(paddedMax)

    // Build y-axis ticks at a dynamic step roughly equal to 1/4 of the range
    const approxQuarter = paddedMax / 4
    const step = getNiceStep(approxQuarter)
    const lastTick = Math.floor(paddedMax / step) * step
    const ticks: number[] = []
    for (let t = step; t <= lastTick; t += step) {
      ticks.push(t)
    }
    setYTicks(ticks)
  }, [data])

  return (
    <div className="chart-container">
      <ResponsiveContainer width="100%" height={210}>
        <BarChart data={chartData} margin={{ top: 10, right: 0, left: -25, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" />
          <YAxis domain={[0, maxValue]} ticks={yTicks} allowDecimals={false} />
          <Tooltip />
          {/* Keep status-based code in comments for future use
          {statuses.map((status, index) => (
            <Bar
              key={status}
              dataKey={status}
              stackId="a"
              fill={colors[index]}
            />
          ))}
          */}
          <Bar dataKey="value" fill="#284F43" radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
